(() => {
  const ENDPOINT = '/apps/republic-rank/missions';
  const PRODUCT_VIEW_DELAY_MS = 3000;

  const requestJson = async (method = 'GET', body = null) => {
    const response = await fetch(ENDPOINT, {
      method,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      cache: 'no-store'
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {
      payload = null;
    }

    if (!response.ok || payload?.success !== true) {
      const error = new Error(payload?.error || 'Daily Missions are unavailable.');
      error.status = response.status;
      error.code = payload?.code || 'MISSIONS_REQUEST_FAILED';
      throw error;
    }

    return payload;
  };

  const readStrings = (root) => {
    const node = root.querySelector('[data-cr-missions-strings]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return {};
    }
  };

  const setWidth = (node, current, target) => {
    if (!node) return;
    const safeTarget = Math.max(1, Number(target) || 1);
    const safeCurrent = Math.max(0, Number(current) || 0);
    const percent = Math.max(0, Math.min(100, (safeCurrent / safeTarget) * 100));
    node.style.width = `${percent}%`;
  };

  const renderMission = (root, key, mission, strings) => {
    const row = root.querySelector(`[data-cr-mission-key="${key}"]`);
    if (!row || !mission) return;

    const status = row.querySelector('[data-cr-mission-status]');
    const progress = row.querySelector('[data-cr-mission-progress]');
    const bar = row.querySelector('[data-cr-mission-bar]');

    row.classList.toggle('is-complete', mission.completed === true);

    if (status) {
      status.textContent = mission.completed === true
        ? (strings.done || 'DONE')
        : (strings.inProgress || 'IN PROGRESS');
    }

    if (progress) {
      progress.textContent = `${Number(mission.progress || 0)}/${Number(mission.target || 1)}`;
    }

    setWidth(bar, mission.progress, mission.target);
  };

  const renderPanel = (root, payload) => {
    const strings = readStrings(root);
    const xp = payload?.xp || {};
    const weekly = payload?.weekly || {};
    const completion = payload?.missions?.dailyCompletion || {};

    root.classList.remove('is-loading', 'is-error');
    root.classList.toggle('is-complete', payload?.dailyComplete === true);

    const xpValue = root.querySelector('[data-cr-missions-xp]');
    const xpBar = root.querySelector('[data-cr-missions-xp-bar]');
    const dailyStatus = root.querySelector('[data-cr-missions-daily-status]');
    const completionStatus = root.querySelector('[data-cr-missions-completion-status]');
    const weeklyCount = root.querySelector('[data-cr-missions-weekly-count]');
    const weeklyBar = root.querySelector('[data-cr-missions-weekly-bar]');
    const weeklyStatus = root.querySelector('[data-cr-missions-weekly-status]');
    const gearLink = root.querySelector('[data-cr-missions-gear-link]');
    const syncNote = root.querySelector('[data-cr-missions-sync-note]');

    const earnedToday = Math.max(0, Number(xp.earnedToday || 0));
    const maxToday = Math.max(1, Number(xp.maxToday || 20));

    if (xpValue) xpValue.textContent = `${earnedToday}/${maxToday} XP`;
    setWidth(xpBar, earnedToday, maxToday);

    if (dailyStatus) {
      dailyStatus.textContent = payload?.dailyComplete === true
        ? (strings.dailyComplete || 'DAILY COMPLETE')
        : (strings.dailyActive || 'ACTIVE');
    }

    renderMission(root, 'hubVisit', payload?.missions?.hubVisit, strings);
    renderMission(root, 'productExplorer', payload?.missions?.productExplorer, strings);
    renderMission(root, 'dailyBooster', payload?.missions?.dailyBooster, strings);

    if (completionStatus) {
      completionStatus.textContent = completion.boosterGranted === true
        ? (strings.boosterAwarded || 'BASIC BOOSTER AWARDED')
        : (strings.boosterLocked || 'COMPLETE ALL 3 MISSIONS');
      completionStatus.classList.toggle('is-awarded', completion.boosterGranted === true);
    }

    const completedDays = Math.max(0, Number(weekly.completedDays || 0));
    const requiredDays = Math.max(1, Number(weekly.requiredDays || 5));

    if (weeklyCount) weeklyCount.textContent = `${completedDays}/${requiredDays}`;
    setWidth(weeklyBar, completedDays, requiredDays);

    if (weeklyStatus) {
      weeklyStatus.textContent = weekly.chestGranted === true
        ? (strings.chestAwarded || 'REPUBLIC CHEST AWARDED')
        : (strings.chestProgress || 'WEEKLY PROGRESS');
      weeklyStatus.classList.toggle('is-awarded', weekly.chestGranted === true);
    }

    if (gearLink) gearLink.hidden = weekly.chestGranted !== true;

    if (syncNote) {
      syncNote.hidden = payload?.xpSyncPending !== true;
      syncNote.textContent = strings.xpPending || '';
    }
  };

  const initMissionPanel = async () => {
    const root = document.querySelector('[data-cr-missions]');
    if (!root) return;

    try {
      const payload = await requestJson('GET');
      renderPanel(root, payload);
    } catch (error) {
      root.classList.remove('is-loading');
      root.classList.add('is-error');

      const strings = readStrings(root);
      const status = root.querySelector('[data-cr-missions-daily-status]');
      const errorCopy = root.querySelector('[data-cr-missions-error]');

      if (status) status.textContent = strings.unavailable || 'UNAVAILABLE';
      if (errorCopy) {
        errorCopy.hidden = false;
        errorCopy.textContent = strings.loadError || 'Daily Missions could not be loaded.';
      }
    }
  };

  const initProductTracking = () => {
    const productRoots = document.querySelectorAll('[data-cr-mission-product-id]');
    if (!productRoots.length) return;

    productRoots.forEach((root) => {
      const productId = String(root.dataset.crMissionProductId || '').trim();
      if (!/^gid:\/\/shopify\/Product\/\d+$/.test(productId)) return;

      if (root.dataset.crMissionTracked === '1') return;
      root.dataset.crMissionTracked = '1';

      window.setTimeout(async () => {
        if (document.visibilityState === 'hidden') return;

        try {
          await requestJson('POST', {
            operation: 'view_product',
            productId
          });

        } catch (error) {
          // Logged-out visitors and temporary network failures are intentionally silent.
          if (error?.status && error.status !== 401) {
            console.debug('Card Republic Daily Missions product view was not recorded.', error);
          }
        }
      }, PRODUCT_VIEW_DELAY_MS);
    });
  };

  const init = () => {
    initMissionPanel();
    initProductTracking();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
